import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../../lib/api';
import { toast } from 'sonner';
import { PlugsConnected, Plugs } from '@phosphor-icons/react';

export const MarketplaceTab = ({ tenantId }) => {
  const qc = useQueryClient();
  const { data: apps } = useQuery({
    queryKey: ['marketplace', tenantId],
    queryFn: () => api.get('/marketplace/apps', { params: tenantId ? { tenant_id: tenantId } : {} }).then((r) => r.data),
    enabled: !!tenantId,
  });
  const connectMut = useMutation({
    mutationFn: ({ app, api_key, org_id }) => api.post('/marketplace/connect', { tenant_id: tenantId, app, api_key, org_id }),
    onSuccess: (r) => { toast.success(`${r.data.app} connected - ${r.data.status}`); qc.invalidateQueries({ queryKey: ['marketplace'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const disconnectMut = useMutation({
    mutationFn: (app) => api.delete(`/marketplace/${app}`, { params: { tenant_id: tenantId } }),
    onSuccess: () => { toast.success('Disconnected'); qc.invalidateQueries({ queryKey: ['marketplace'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const doConnect = (a) => {
    const api_key = window.prompt(`${a.name} API key / auth token (stored encrypted):`) || '';
    const org_id = window.prompt('Organization ID (optional):') || '';
    connectMut.mutate({ app: a.key, api_key, org_id });
  };
  const download = async (path, fname) => {
    try {
      const r = await api.get(path, { params: { tenant_id: tenantId }, responseType: 'blob' });
      const url = window.URL.createObjectURL(r.data);
      const el = document.createElement('a');
      el.href = url; el.download = fname; el.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="marketplace-panel">
      <p className="text-sm text-slate-500 mb-4">Connect Zoho One and other business suites. Credentials are encrypted at rest - live two-way sync activates when vendor webhooks are configured (currently export-based, STUBBED).</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {(apps || []).map((a) => (
          <div key={a.key} className="bg-white border border-slate-200 rounded-md p-4 card-hover" data-testid={`mp-app-${a.key}`}>
            <div className="flex items-center gap-2">
              {a.connected ? <PlugsConnected size={18} className="text-green-600" /> : <Plugs size={18} className="text-slate-300" />}
              <p className="font-display font-semibold text-sm">{a.name}</p>
              <span className="ms-auto font-mono-label text-[9px] uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{a.category}</span>
            </div>
            <p className="text-xs text-slate-500 mt-1.5">{a.sync}</p>
            <p className={`font-mono-label text-[9px] uppercase mt-2 ${a.connected ? 'text-green-700' : 'text-slate-400'}`} data-testid={`mp-status-${a.key}`}>{a.status}</p>
            <div className="flex gap-2 mt-3 flex-wrap">
              {a.connected
                ? <button data-testid={`mp-disconnect-${a.key}`} onClick={() => disconnectMut.mutate(a.key)} className="text-xs border border-rose-200 text-rose-600 rounded px-2.5 py-1 hover:bg-rose-50">Disconnect</button>
                : <button data-testid={`mp-connect-${a.key}`} onClick={() => doConnect(a)} className="text-xs bg-slate-900 text-white rounded px-2.5 py-1 hover:bg-slate-800">Connect</button>}
              {a.export === 'zoho-books' && <button data-testid={`mp-export-books-${a.key}`} onClick={() => download('/marketplace/exports/zoho-books.csv', 'zoho-books-invoices.csv')} className="text-xs border border-slate-200 rounded px-2.5 py-1 hover:border-slate-900">Export invoices</button>}
              {a.export === 'zoho-people' && <button data-testid={`mp-export-people-${a.key}`} onClick={() => download('/marketplace/exports/zoho-people.csv', 'zoho-people-staff.csv')} className="text-xs border border-slate-200 rounded px-2.5 py-1 hover:border-slate-900">Export staff</button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
